import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Loader2, LogOut, Newspaper, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AdminTabs from "@/components/AdminTabs";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
    : "—";

const Chip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1.5 text-[11px] font-archivo uppercase tracking-widest border ${
      active ? "bg-bb-navy text-white border-bb-navy" : "bg-white text-bb-navy border-border"
    }`}
  >
    {label}
  </button>
);

export default function AdminDraftsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    const qs = status === "all" ? "" : `?status=${status}`;
    api
      .get(`/admin/drafts${qs}`, { withCredentials: true })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load drafts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [status]);

  const logout = async () => {
    try { await api.post("/auth/logout", null, { withCredentials: true }); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  const setRowBusy = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const publish = async (id) => {
    setRowBusy(id, true);
    try {
      await api.post(`/admin/drafts/${id}/publish`, null, { withCredentials: true });
      toast.success("Published — live on /news");
      load();
    } catch { toast.error("Publish failed"); } finally { setRowBusy(id, false); }
  };

  const unpublish = async (id) => {
    setRowBusy(id, true);
    try {
      await api.post(`/admin/drafts/${id}/unpublish`, null, { withCredentials: true });
      toast.success("Unpublished");
      load();
    } catch { toast.error("Unpublish failed"); } finally { setRowBusy(id, false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this draft? This cannot be undone.")) return;
    setRowBusy(id, true);
    try {
      await api.delete(`/admin/drafts/${id}`, { withCredentials: true });
      toast.success("Deleted");
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch { toast.error("Delete failed"); } finally { setRowBusy(id, false); }
  };

  return (
    <div data-testid="admin-drafts-page" className="bg-bb-offwhite min-h-screen">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="bb-container py-3 flex items-center justify-between gap-3">
          <div>
            <div className="bb-overline text-bb-copper">Admin · Drafts</div>
            <div className="font-archivo font-bold text-bb-navy text-lg leading-tight">
              Editorial CMS — {items.length} {items.length === 1 ? "entry" : "entries"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => nav("/admin/drafts/new")}
              data-testid="admin-new-draft"
              className="bb-btn-primary inline-flex items-center gap-2 px-3 py-2 text-[11px] min-h-[44px]"
            >
              <Plus size={13} /> <span className="hidden sm:inline">New draft</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1 px-3 py-2 text-[11px] font-archivo uppercase tracking-widest text-bb-slate hover:text-bb-navy"
            >
              <LogOut size={13} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
        <AdminTabs />
        <div className="bb-container py-2 flex gap-2 overflow-x-auto">
          {[["all", "All"], ["draft", "Draft"], ["published", "Published"]].map(([v, l]) => (
            <Chip
              key={v}
              label={l}
              active={status === v}
              onClick={() => setStatus(v)}
              testId={`drafts-status-${v}`}
            />
          ))}
        </div>
      </header>

      <div className="bb-container py-4 space-y-3">
        {loading ? (
          <div className="text-center py-10 bb-overline text-bb-slate">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-border p-8 text-center">
            <Newspaper size={20} className="text-bb-copper mx-auto" />
            <div className="bb-overline text-bb-copper mt-3">Nothing here yet</div>
            <p className="text-sm text-bb-slate mt-2">
              Click <span className="font-semibold text-bb-navy">New draft</span> to write your first article.
            </p>
          </div>
        ) : (
          items.map((d) => {
            const isBusy = !!busy[d.id];
            const published = d.status === "published";
            return (
              <article
                key={d.id}
                className="bg-white border border-border p-4 flex flex-col gap-3"
                data-testid={`admin-draft-${d.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="bb-overline text-bb-copper">{d.category}</span>
                    <span
                      className={`text-[10px] font-mono px-2 py-0.5 ${
                        published ? "bb-tag-positive" : "bb-tag-neutral"
                      }`}
                      data-testid={`draft-status-${d.id}`}
                    >
                      {published ? "PUBLISHED" : "DRAFT"}
                    </span>
                  </div>
                  <span className="text-[11px] font-mono text-bb-slate">Updated {fmt(d.updatedAt)}</span>
                </div>
                <h3 className="font-archivo text-base sm:text-lg text-bb-navy font-semibold leading-snug">
                  {d.title}
                </h3>
                {d.excerpt && (
                  <p className="text-sm text-bb-slate leading-relaxed">{d.excerpt}</p>
                )}
                <div className="text-[11px] font-mono text-bb-slate flex flex-wrap gap-x-3 gap-y-1">
                  <span>slug: {d.slug}</span>
                  <span>author: {d.author || "—"}</span>
                  {d.region && <span>region: {d.region}</span>}
                  {d.commodity && <span>commodity: {d.commodity}</span>}
                </div>
                <div className="flex items-center justify-between gap-2 pt-3 border-t border-border flex-wrap">
                  <div className="flex items-center gap-2">
                    {published && (
                      <Link
                        to={`/news/${d.slug}`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-navy bb-link"
                        data-testid={`draft-${d.id}-view`}
                      >
                        <ExternalLink size={11} /> View live
                      </Link>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => remove(d.id)}
                      disabled={isBusy}
                      data-testid={`draft-${d.id}-delete`}
                      className="inline-flex items-center gap-1 px-3 py-2.5 border border-border text-bb-slate text-sm font-archivo uppercase tracking-widest disabled:opacity-50 min-h-[44px]"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                    <Link
                      to={`/admin/drafts/${d.id}`}
                      data-testid={`draft-${d.id}-edit`}
                      className="inline-flex items-center gap-1 px-3 py-2.5 border border-border text-bb-navy text-sm font-archivo uppercase tracking-widest min-h-[44px]"
                    >
                      <Pencil size={13} /> Edit
                    </Link>
                    {published ? (
                      <button
                        onClick={() => unpublish(d.id)}
                        disabled={isBusy}
                        data-testid={`draft-${d.id}-unpublish`}
                        className="inline-flex items-center gap-1 px-3 py-2.5 bg-white border border-bb-copper text-bb-copper text-sm font-archivo uppercase tracking-widest disabled:opacity-50 min-h-[44px]"
                      >
                        {isBusy ? <Loader2 size={13} className="animate-spin" /> : null} Unpublish
                      </button>
                    ) : (
                      <button
                        onClick={() => publish(d.id)}
                        disabled={isBusy}
                        data-testid={`draft-${d.id}-publish`}
                        className="bb-btn-primary inline-flex items-center gap-1 px-3 py-2.5 text-sm disabled:opacity-50 min-h-[44px]"
                      >
                        {isBusy ? <Loader2 size={13} className="animate-spin" /> : null} Publish
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>

      <div className="bb-container py-6 text-center">
        <Link to="/" className="text-xs font-archivo uppercase tracking-widest text-bb-slate bb-link">
          Back to site
        </Link>
      </div>
    </div>
  );
}
